import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, MessageCircle, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { useSiteSettings } from "@/hooks/useSiteSettings";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";

const INQUIRY_TYPES = [
  { id: "compra", label: "Compra de viatura" },
  { id: "importacao", label: "Importação da Europa" },
  { id: "financiamento", label: "Financiamento" },
  { id: "outro", label: "Outro assunto" },
];

const initialForm = { name: "", email: "", phone: "", inquiry_type: "compra", vehicle_id: null, message: "" };

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [consent, setConsent] = useState({ accept_policy: false, accept_marketing: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const settings = useSiteSettings();
  const PHONE_TEL = `tel:${settings.phone.replace(/\s/g, "")}`;
  const WHATSAPP_URL = `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`;

  useEffect(() => {
    const onVehicle = (e) => {
      const v = e.detail;
      setForm((f) => ({
        ...f,
        inquiry_type: "compra",
        vehicle_id: v.id,
        message: f.message || `Tenho interesse na viatura "${v.title}". Gostaria de receber mais informações.`,
      }));
    };
    const onInquiry = (e) => {
      setForm((f) => ({
        ...f,
        inquiry_type: e.detail,
        message:
          f.message ||
          "Gostaria de receber uma proposta de financiamento com base na simulação que realizei no site.",
      }));
    };
    const prefillInquiry = sessionStorage.getItem("lead_inquiry");
    if (prefillInquiry) {
      sessionStorage.removeItem("lead_inquiry");
      onInquiry({ detail: prefillInquiry });
    }
    window.addEventListener("lead:vehicle", onVehicle);
    window.addEventListener("lead:inquiry", onInquiry);
    return () => {
      window.removeEventListener("lead:vehicle", onVehicle);
      window.removeEventListener("lead:inquiry", onInquiry);
    };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      try {
        await axios.post(`${API}/privacy/consent`, {
          name: form.name, email: form.email,
          accept_policy: consent.accept_policy, accept_marketing: consent.accept_marketing,
          context: "contacto", policy_version: settings.privacy_policy_version || "1.0",
        });
      } catch { /* consent registado em segundo plano */ }
      setSent(true);
      toast.success("Pedido enviado com sucesso! Entraremos em contacto em breve.");
      setForm(initialForm);
      setConsent({ accept_policy: false, accept_marketing: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar o pedido. Verifique os dados e tente novamente."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-20 sm:py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Contact channels */}
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.18em] sm:tracking-[0.35em] uppercase text-slate-500 mb-4 break-words">
              Fale Connosco
            </p>
            <h2
              data-testid="contact-heading"
              className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6] leading-tight"
            >
              Peça a Sua Cotação
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Compra, importação ou financiamento — escolha a forma de contacto que preferir.
              Respondemos com a maior brevidade possível.
            </p>

            <div className="mt-10 space-y-4" data-testid="contact-channels">
              <a
                href={PHONE_TEL}
                data-testid="contact-call-button"
                className="flex items-center gap-4 bg-white border border-slate-200 p-5 hover:border-[#0072C6]/40 hover:shadow-sm transition-all group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                  <Phone className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Ligar agora</p>
                  <p className="font-semibold text-[#0072C6] group-hover:underline">{settings.phone}</p>
                </div>
              </a>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="flex items-center gap-4 bg-white border border-slate-200 p-5 hover:border-[#0072C6]/40 hover:shadow-sm transition-all group"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">WhatsApp</p>
                  <p className="font-semibold text-[#0072C6] group-hover:underline">{settings.phone}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 bg-white border border-slate-200 p-5" data-testid="contact-email-row">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                  <Mail className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Email</p>
                  <p className="font-semibold text-[#0072C6]">{settings.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white border border-slate-200 p-5" data-testid="contact-address-row">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white shrink-0">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Stand</p>
                  <p className="font-semibold text-[#0072C6]">{settings.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-slate-200 shadow-sm p-7 sm:p-10"
            >
              <h3 className="font-display text-2xl text-[#0072C6]">Formulário de Contacto</h3>
              <p className="mt-2 text-sm text-slate-500">
                Preencha os campos abaixo e entraremos em contacto consigo.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="lead-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Nome *
                  </label>
                  <input
                    id="lead-name"
                    data-testid="lead-name-input"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="O seu nome"
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lead-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Telefone *
                  </label>
                  <input
                    id="lead-phone"
                    data-testid="lead-phone-input"
                    type="tel"
                    required
                    minLength={6}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+351 ..."
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lead-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Email *
                  </label>
                  <input
                    id="lead-email"
                    data-testid="lead-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="oseuemail@exemplo.pt"
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lead-inquiry" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Assunto *
                  </label>
                  <select
                    id="lead-inquiry"
                    data-testid="lead-inquiry-select"
                    value={form.inquiry_type}
                    onChange={set("inquiry_type")}
                    className="w-full border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="lead-message" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                    Mensagem
                  </label>
                  <textarea
                    id="lead-message"
                    data-testid="lead-message-textarea"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Conte-nos o que procura: modelo, orçamento, prazo…"
                    className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors resize-y"
                  />
                </div>
              </div>

              <div className="mt-6">
                <ConsentCheckboxes consent={consent} onChange={setConsent} idPrefix="contact" />
              </div>

              {sent && (
                <div
                  className="mt-6 flex items-center gap-2.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3"
                  data-testid="lead-success-message"
                >
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  Pedido recebido. Entraremos em contacto em breve.
                </div>
              )}

              <button
                type="submit"
                data-testid="lead-submit-button"
                disabled={sending}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#e05e00] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "A enviar…" : "Enviar Pedido"}
              </button>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                Ao submeter, aceita ser contactado pela SOLUTION CAR PREMIUM relativamente ao seu pedido.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
